import React, { useState, useMemo, useEffect } from "react";
import {
  CCard,
  CCardHeader,
  CButton,
  CListGroup,
  CListGroupItem,
  CBadge
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilCloudUpload, cilMediaPlay, cilFolder, cilFolderOpen } from "@coreui/icons";
import dayjs from "dayjs";
import { getDeviceOffsetMin } from "../utils/timeUtils";

const CameraArchiveList = ({
  t,
  isStaff,
  toast,
  setUploadModalVisible,
  folders,
  selectedVideo,
  handleVideoSelect,
  timezoneOffset,
}) => {
  // Состояние для хранения открытых/закрытых дат (по умолчанию все закрыты)
  const [expandedDates, setExpandedDates] = useState({});

  // Группируем плоский массив папок по датам
  const groupedFolders = useMemo(() => {
    const groups = {};

    folders.forEach((folder) => {
      const end = dayjs(folder.process_after).utcOffset(getDeviceOffsetMin(timezoneOffset));
      const start = end.subtract(1, "hour");

      const dateKey = start.format("DD.MM.YYYY");

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }

      // Добавляем отформатированные часы для отрисовки дочерних элементов
      groups[dateKey].push({
        ...folder,
        startHour: start.format("HH:00"),
        endHour: end.format("HH:00"),
      });
    });

    return groups;
  }, [folders, timezoneOffset]);

  // При первой загрузке автоматически раскрываем самую первую (свежую) дату
  useEffect(() => {
    const dates = Object.keys(groupedFolders);
    if (dates.length > 0 && Object.keys(expandedDates).length === 0) {
      setExpandedDates({ [dates[0]]: true });
    }
  }, [groupedFolders]);

  const toggleDate = (dateKey) => {
    setExpandedDates((prev) => ({
      ...prev,
      [dateKey]: !prev[dateKey],
    }));
  };

  return (
    <CCard className="archive-match-height d-flex flex-column border-0 shadow-sm w-100">
      <CCardHeader className="d-flex justify-content-between align-items-center bg-white border-bottom flex-shrink-0 py-2">
        <span className="fw-bold">{t("camera.video_archive", "Видеоархив")}</span>
        <CButton
          color="dark"
          size="sm"
          variant="outline"
          title={t("camera_modals.btn_upload_main")}
          onClick={() => {
            if (!isStaff) {
              toast.warn(t("common.role_restricted"));
              return;
            }
            setUploadModalVisible(true);
          }}
        >
          <CIcon icon={cilCloudUpload} />
        </CButton>
      </CCardHeader>

      {/* Обертка для скролла, чтобы карточка не растягивалась */}
      <div className="archive-scroll-container flex-grow-1" style={{ overflowY: "auto", minHeight: 0 }}>
        <CListGroup flush>
          {Object.entries(groupedFolders).map(([dateKey, dailyFolders]) => (
            <React.Fragment key={dateKey}>
              {/* ЗАГОЛОВОК ДНЯ (ПАПКА) */}
              <CListGroupItem
                action
                color="light"
                onClick={() => toggleDate(dateKey)}
                className="d-flex justify-content-between align-items-center fw-bold"
                style={{ cursor: "pointer", borderLeft: "4px solid #321fdb" }}
              >
                <div className="d-flex align-items-center">
                  <CIcon 
                    icon={expandedDates[dateKey] ? cilFolderOpen : cilFolder} 
                    className="me-2 text-primary" 
                    size="lg" 
                  />
                  {dateKey}
                </div>
                <CBadge color="secondary" shape="rounded-pill">
                  {dailyFolders.length}
                </CBadge>
              </CListGroupItem>

              {/* СПИСОК ЧАСОВ ДЛЯ ЭТОГО ДНЯ (Разворачивается при клике) */}
              {expandedDates[dateKey] &&
                dailyFolders.map((folder) => {
                  const isActive = selectedVideo === folder.video_file;
                  
                  return (
                    <CListGroupItem
                      key={folder.id}
                      action
                      active={isActive}
                      onClick={() => handleVideoSelect(folder)}
                      className="d-flex justify-content-between align-items-center ps-4"
                      style={{ cursor: "pointer" }}
                    >
                      <span className="fw-medium">
                        {folder.startHour} - {folder.endHour}
                      </span>
                      <div className="text-end">
                        <CBadge 
                          color={isActive ? "light" : "secondary"} 
                          textColor={isActive ? "dark" : "white"}
                          shape="rounded-pill"
                          className="d-flex align-items-center shadow-sm"
                        >
                          <CIcon icon={cilMediaPlay} className="me-1" size="sm" />
                          {folder.file_count} ф.
                        </CBadge>
                      </div>
                    </CListGroupItem>
                  );
                })}
            </React.Fragment>
          ))}
          
          {Object.keys(groupedFolders).length === 0 && (
            <CListGroupItem className="text-center text-muted p-4">
              Архив пуст
            </CListGroupItem>
          )}
        </CListGroup>
      </div>
    </CCard>
  );
};

export default CameraArchiveList;